/**
 * The caller's own tenant, and the one question the console keeps asking of it.
 *
 * A school admin is not a platform engineer (multi-tenancy/tenant-admin-console.md).
 * Several surfaces here are the PLATFORM's — the global taxonomy, the shared
 * bookmark pool, the product dashboards — and they are withheld from an admin
 * who administers one customer. That distinction was about to be re-derived at
 * every call site, so it lives here once: the same query key the shell already
 * used, plus the predicate spelled out.
 *
 * `undefined` while the query is in flight, and `false` if it fails. Both mean
 * "carry on as before" rather than blanking the console on a hiccup — this is a
 * PRESENTATION rule, and the endpoints are what actually enforce tenancy. A
 * surface that would leak another tenant's data if shown must be fixed on the
 * server, not hidden here.
 */
import { useQuery } from '@tanstack/react-query';
import { tenantsApi, type MyTenantInfo } from '../api/tenantsApi';

/** '0' — the public tenant every unscoped record belongs to. */
export const PUBLIC_TENANT_TYPE = 'public';
/** The synthetic tenant a super-admin session resolves to (no tenant doc). */
export const PLATFORM_TENANT_TYPE = 'platform';

export function useMyTenant(): MyTenantInfo | undefined {
    const { data } = useQuery({
        queryKey: ['my-tenant'],
        queryFn: () => tenantsApi.getMine(),
        staleTime: 5 * 60 * 1000,
    });
    return data;
}

/**
 * Is the caller administering a real customer tenant — a school — rather than
 * the public directory or the platform itself?
 *
 * This is the gate behind every "not for a school admin" surface. It reads the
 * tenant's TYPE, not the id: '0' is the public tenant and 'platform' is the
 * super-admin's synthetic one, and neither is somebody's school.
 */
export function useIsNonPublicTenant(): boolean {
    const tenant = useMyTenant();
    if (!tenant) return false;
    return tenant.type !== PUBLIC_TENANT_TYPE && tenant.type !== PLATFORM_TENANT_TYPE;
}
